/* REACT AND CSS */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import useFAQModalState from '../states/useFAQModalState'
import { Button, Form, Icon, Input, Modal, TextArea } from 'semantic-ui-react'
import { NotificationManager } from 'react-notifications'

/**
 * <FAQModal    type
 *              faq [OPTIONAL]
 *              faqKey [OPTIONAL]
 *              faqIndex [OPTIONAL]
 *              handleAction />
 * 
 * @prop { string } type - action type
 * @prop { object } faq - faq object
 * @prop { string } faqKey - object key
 * @prop { integer } faqIndex - index of faq
 * @prop { function } handleAction - function to manipulate faq
 */
function FAQModal(props) {
    const { type, faq, faqKey, faqIndex } = props;
    const {
        formState,
        modal,
        updateFormState,
        updateEditModal,
        updateDeleteModal,
        resetModal,
        resetForm
    } = useFAQModalState();

    /**
     * Initialize data upon editing data.
     */
    const setData = () => {
        if(modal.edit || modal.delete) {
            if (type === 'edit') {
                updateFormState(faq.question, faq.answer);
            } else {
                resetForm();
            }
        }
    }

    useEffect(setData, [faq, type, modal]);

    /**
     * This function handles creating the FAQ.
     */
    const handleCreate = () => {
        props.handleAction(formState);
        resetModal();
    }

    /**
     * This function handles editing the FAQ.
     */
    const handleEdit = () => {
        if (formState.question.trim() === faq.question.trim() &&
            formState.answer.trim() === faq.answer.trim()) {
            NotificationManager.info('No changes made.', '', 2000);
        } else {
            props.handleAction(faqKey, formState);
        }
        resetModal();
    }

    /**
     * This function handles deleting the FAQ.
     */
    const handleDelete = () => {
        props.handleAction(faqKey);
        resetModal();
    }

    /**
     * This function handles changing values of the fields.
     * @param { event } event - onChange event
     * @param { string } name - name of the component
     * @param { string } value - new value of the field
     */
    const handleChange = (event, { name, value }) => {
        let formStateClone = {...formState};
        formStateClone[name] = value;
        updateFormState(formStateClone.question, formStateClone.answer);
    }

    return (
        type === 'create' || type === 'edit' ? 
            <Modal
                open={modal.edit}
                onClose={resetModal}
                closeOnDimmerClick={false}
                closeOnEscape={false}
                centered
                size='tiny'
                className='web-modal'
                trigger={
                    type === 'create' ?
                        <Button
                            id='create-faq'
                            className='page-buttons'
                            content='Add'
                            floated='right'
                            onClick={() => updateEditModal(true)} />
                    :
                        <Icon
                            id={`edit-faq-${faqIndex}`}
                            link
                            size='small'
                            name='pencil'
                            onClick={() => updateEditModal(true)} />
                }>
                <Modal.Header className='modal-header'>{type === 'create' ? 'Create FAQ' : 'Edit FAQ'}</Modal.Header>
                <Modal.Content>
                    <Form onSubmit={type === 'create' ? handleCreate : handleEdit}>
                        <Form.Field 
                            required
                            id='faq-question'
                            control={Input}
                            type='text'
                            label='Question'
                            placeholder='Question'
                            name='question'
                            value={formState.question}
                            onChange={handleChange} />
                        <Form.Field 
                            required
                            id='faq-answer'
                            control={TextArea}
                            type='text'
                            label='Answer'
                            placeholder='Answer'
                            name='answer'
                            value={formState.answer}
                            onChange={handleChange} />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type='submit'
                        id='update-faq-button'
                        className='modal-buttons'
                        floated='right'
                        disabled={formState.question.trim() === '' || formState.answer.trim() === ''}
                        content={type === 'create' ? 'Create' : 'Update'}
                        onClick={type === 'create' ? handleCreate : handleEdit} />
                    <Button 
                        type='button'
                        id='cancel-update-faq-button'
                        floated='right'
                        content='Cancel'
                        onClick={resetModal} />
                </Modal.Actions>
            </Modal>
        :
            <Modal
                open={modal.delete}
                onClose={resetModal}
                closeOnDimmerClick={false}
                closeOnEscape={false}
                centered
                className='web-modal'
                size='mini'
                trigger={
                    <Icon
                        id={`delete-faq-${faqIndex}`}
                        link
                        size='small'
                        name='trash'
                        onClick={() => updateDeleteModal(true)} />
                }>
                <Modal.Header className='modal-header'>Delete FAQ</Modal.Header>
                <Modal.Content>
                    Are you sure you want to delete <strong>{faq.question}</strong>?
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type='button'
                        id='delete-faq-button'
                        className='modal-buttons'
                        floated='right'
                        content='Delete'
                        onClick={handleDelete} />
                    <Button 
                        type='button'
                        id='cancel-delete-faq-button'
                        floated='right'
                        content='Cancel'
                        onClick={resetModal} />
                </Modal.Actions>
            </Modal>
    )
}

FAQModal.propTypes = {
    type: PropTypes.string,
    faqKey: PropTypes.string,
    faq: PropTypes.object,
    faqIndex: PropTypes.number,
    handleAction: PropTypes.func
}

export default FAQModal;